import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Building2, Plus, Trash2, Users, Shield, UserPlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function CompanyManagement({ user }) {
    const [companies, setCompanies] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState('');
    const [showAdminPicker, setShowAdminPicker] = useState(null);

    useEffect(() => {
        fetchCompanies();
        fetchAllUsers();
    }, []);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/admin/companies`, { withCredentials: true });
            setCompanies(response.data);
        } catch (error) {
            toast.error('Erro ao carregar empresas');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
            setAllUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleCreate = async () => {
        if (!newName.trim()) {
            toast.error('Nome da empresa é obrigatório');
            return;
        }
        try {
            await axios.post(`${API}/admin/companies`, { name: newName.trim() }, { withCredentials: true });
            toast.success('Empresa criada!');
            setNewName('');
            setShowCreate(false);
            fetchCompanies();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar empresa');
        }
    };

    const handleDelete = async (companyId, name) => {
        if (!window.confirm(`Tem certeza que deseja excluir "${name}"?`)) return;
        try {
            await axios.delete(`${API}/admin/companies/${companyId}`, { withCredentials: true });
            toast.success('Empresa excluída');
            fetchCompanies();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir empresa');
        }
    };

    const handleAdminAction = async (companyId, userId, action) => {
        try {
            await axios.put(`${API}/admin/companies/${companyId}/admins`, {
                user_id: userId,
                action: action
            }, { withCredentials: true });
            toast.success(action === 'add' ? 'Admin adicionado!' : 'Admin removido!');
            fetchCompanies();
            fetchAllUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar admins');
        }
    };

    if (user?.role !== 'super_admin') {
        return (
            <Card className="p-8 rounded-none border border-border text-center">
                <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Apenas Super Admin pode gerenciar empresas</p>
            </Card>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Empresas ({companies.length})
                </h2>
                <Button
                    onClick={() => setShowCreate(!showCreate)}
                    className="rounded-none font-medium tracking-wide uppercase text-xs"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Empresa
                </Button>
            </div>

            {showCreate && (
                <Card className="p-4 rounded-none border border-border mb-6">
                    <div className="flex gap-3">
                        <Input
                            placeholder="Nome da empresa"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className="rounded-none flex-1"
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                        />
                        <Button onClick={handleCreate} className="rounded-none">Criar</Button>
                        <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-none">Cancelar</Button>
                    </div>
                </Card>
            )}

            <div className="grid gap-4">
                {companies.map(company => {
                    const companyAdmins = allUsers.filter(u => company.admin_ids?.includes(u.user_id));
                    const nonAdminUsers = allUsers.filter(u =>
                        u.company_id === company.company_id &&
                        !company.admin_ids?.includes(u.user_id) &&
                        u.status === 'approved'
                    );

                    return (
                        <Card key={company.company_id} className="p-6 rounded-none border border-border">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-primary/10 rounded-none">
                                        <Building2 className="h-5 w-5" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg">{company.name}</h3>
                                        <p className="text-xs text-muted-foreground">
                                            {company.user_count || 0} usuários · ID: {company.company_id}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowAdminPicker(showAdminPicker === company.company_id ? null : company.company_id)}
                                        className="rounded-none"
                                    >
                                        <UserPlus className="h-4 w-4 mr-1" />
                                        Admins
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(company.company_id, company.name)}
                                        className="rounded-none text-red-500 hover:text-red-400"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Current Admins */}
                            {companyAdmins.length > 0 && (
                                <div className="mb-3">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Admins/Instrutores</p>
                                    <div className="flex flex-wrap gap-2">
                                        {companyAdmins.map(admin => (
                                            <span
                                                key={admin.user_id}
                                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs rounded-sm"
                                            >
                                                <Shield className="h-3 w-3" />
                                                {admin.name}
                                                <button
                                                    onClick={() => handleAdminAction(company.company_id, admin.user_id, 'remove')}
                                                    className="ml-1 hover:text-red-500"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Admin Picker */}
                            {showAdminPicker === company.company_id && (
                                <div className="mt-3 p-3 border border-border bg-muted/30">
                                    <p className="text-xs text-muted-foreground mb-2">Selecione um usuário para tornar admin:</p>
                                    <div className="max-h-40 overflow-y-auto space-y-1">
                                        {nonAdminUsers.length === 0 ? (
                                            <p className="text-xs text-muted-foreground italic">Nenhum usuário disponível nesta empresa</p>
                                        ) : (
                                            nonAdminUsers.map(u => (
                                                <button
                                                    key={u.user_id}
                                                    onClick={() => handleAdminAction(company.company_id, u.user_id, 'add')}
                                                    className="w-full text-left px-2 py-1 text-sm hover:bg-muted/50 flex items-center gap-2"
                                                >
                                                    {u.picture && <img src={u.picture} alt="" className="h-5 w-5 rounded-full" />}
                                                    <span>{u.name}</span>
                                                    <span className="text-xs text-muted-foreground">{u.email}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </Card>
                    );
                })}
            </div>

            {companies.length === 0 && !loading && (
                <Card className="p-12 rounded-none border border-border text-center">
                    <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-bold mb-2">Nenhuma empresa cadastrada</h3>
                    <p className="text-muted-foreground">Crie a primeira empresa para começar</p>
                </Card>
            )}
        </div>
    );
}
